import { useEffect, useState } from "react";
import { api, ROLE_LABELS } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/shared";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Save, User, Mail, Phone, MapPin, Lock } from "lucide-react";
import { toast } from "sonner";

export default function ClientProfile() {
  const { user } = useAuth();
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", address: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/client/profile").then((r) => setForm({
      full_name: r.data.full_name || "", email: r.data.email || "",
      phone: r.data.phone || "", address: r.data.address || "",
    })).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    try { await api.put("/client/profile", form); toast.success("Profile updated"); }
    catch (e) { toast.error("Save failed"); }
    setSaving(false);
  };

  return (
    <div>
      <PageHeader title="My Profile" subtitle="View and update your contact details." />
      <div className="max-w-2xl space-y-6">
        <Card className="border-border">
          <CardHeader><CardTitle className="font-heading text-lg flex items-center gap-2"><User size={18} strokeWidth={1.5} /> Contact Details</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-1.5">
              <Label className="flex items-center gap-1.5"><User size={13} /> Full name</Label>
              <Input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} data-testid="profile-name" />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="grid gap-1.5">
                <Label className="flex items-center gap-1.5"><Mail size={13} /> Email</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="profile-email" />
              </div>
              <div className="grid gap-1.5">
                <Label className="flex items-center gap-1.5"><Phone size={13} /> Phone</Label>
                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="profile-phone" />
              </div>
            </div>
            <div className="grid gap-1.5">
              <Label className="flex items-center gap-1.5"><MapPin size={13} /> Address</Label>
              <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="profile-address" />
            </div>
            <Button onClick={save} disabled={saving} className="gap-2" data-testid="profile-save-btn"><Save size={16} strokeWidth={1.5} /> {saving ? "Saving..." : "Save changes"}</Button>
          </CardContent>
        </Card>

        <Card className="border-border bg-muted/30">
          <CardHeader><CardTitle className="font-heading text-lg flex items-center gap-2"><Lock size={16} strokeWidth={1.5} /> Account</CardTitle></CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div className="flex items-center justify-between"><span className="text-muted-foreground">Login email</span><span className="font-medium">{user?.email}</span></div>
            <div className="flex items-center justify-between"><span className="text-muted-foreground">Account type</span><Badge variant="outline" className="bg-secondary text-secondary-foreground border-transparent">{ROLE_LABELS[user?.role]}</Badge></div>
            <p className="text-xs text-muted-foreground pt-2">Your role and training location are managed by Hundeverket and can't be changed here.</p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
